import * as React from 'react'

import { Article, PartialArticle } from '@doist/help-center-utils'
import {
    Box,
    Container,
    getPlatform,
    HelpCenterContextProvider,
    HelpCenterFeedback,
    HelpCenterFeedbackSteps,
    HtmlArticle,
    Platform,
    TOCDesktop,
    TOCMobile,
} from '@doist/marketist'

import { useTranslation } from 'next-i18next'
import { useHelpCenterLabels } from 'services/hooks'
import { useLoginLink } from 'services/loginLink'
import { CSRFHEADER } from 'services/withCSRFValidation'
import { User } from 'types'

import { RelatedArticles } from '../RelatedArticles/RelatedArticles'
import { TagList } from '../TagList/TagList'

import css from './ArticleContent.module.css'

const ARTICLE_FEEDBACK_ID = 'feedback'

type ArticleContentProps = {
    user?: User
    article: Article
    relatedArticles: PartialArticle[]
}

export function ArticleContent({ user, article, relatedArticles }: ArticleContentProps) {
    const { t, i18n } = useTranslation('helpcenter')
    const hcLabels = useHelpCenterLabels()
    const articleId = `article-${article.id}`
    const loginLink = useLoginLink({
        queryString: `success_page=${article.href}#${ARTICLE_FEEDBACK_ID}`,
    })

    const [currentPlatform, setCurrentPlatform] = React.useState<string>()
    const isTOCDisabled = article.labels.includes('toc-disabled')

    React.useEffect(() => {
        const { platform, userAgent } = window.navigator
        switch (getPlatform(userAgent, platform)) {
            case Platform.iOS:
                setCurrentPlatform('ios')
                break
            case Platform.android:
                setCurrentPlatform('android')
                break
            default:
                setCurrentPlatform('web mac windows-legacy')
        }
    }, [])

    async function sendFeedback(positive: boolean, reason?: string) {
        if (!user) {
            return
        }
        return await fetch('/nextapi/help/vote', {
            method: 'POST',
            body: JSON.stringify({
                articleTitle: article.title,
                articleId: article.id,
                positive,
                reason,
                lang: i18n.language,
            }),
            headers: {
                'Content-Type': 'application/json',
                'TWIST-API-TOKEN': user.token,
                ...CSRFHEADER,
            },
        })
    }

    function onFeedbackLogin() {
        window.open(loginLink, '_self')
    }

    return (
        <HelpCenterContextProvider value={hcLabels}>
            <Container className={css.articleContent} padded>
                <aside>
                    {article.platforms.length > 0 && (
                        <TagList
                            title={t('article.platforms')}
                            platforms={article.platforms.map((p) => t('article.' + p))}
                        />
                    )}

                    {!isTOCDisabled && (
                        <TOCDesktop
                            title={t('article.tableOfContents')}
                            targetId={articleId}
                            defaultTopLinkTitle={t('article.instructions')}
                            contentMaxHeight="50vh"
                        />
                    )}

                    <RelatedArticles articles={relatedArticles} />
                </aside>

                <Box className={css.rightSide}>
                    <HtmlArticle
                        theme="twist"
                        id={articleId}
                        html={article.body}
                        initialPlatform={currentPlatform}
                        onPlatformChanged={setCurrentPlatform}
                    />

                    {article.voteEnabled && (
                        <HelpCenterFeedback
                            key={`hc-feedback-${user?.id}`}
                            id={ARTICLE_FEEDBACK_ID}
                            theme="twist"
                            className={css.feedback}
                            onLogin={onFeedbackLogin}
                            initialFlowState={
                                user
                                    ? HelpCenterFeedbackSteps.Choices
                                    : HelpCenterFeedbackSteps.Login
                            }
                            onPositiveFeedback={() => sendFeedback(true)}
                            onNegativeFeedback={(reason: string) => sendFeedback(false, reason)}
                        />
                    )}
                </Box>

                {!isTOCDisabled && (
                    <TOCMobile
                        theme="twist"
                        className={css.mobileToc}
                        title={t('article.tableOfContents')}
                        targetId={articleId}
                        defaultTopLinkTitle={t('article.instructions')}
                    />
                )}
            </Container>
        </HelpCenterContextProvider>
    )
}
